import React, { useState, useEffect } from 'react';
import { ShoppingCart, Search, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { products, categories } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import StockBadge from '../components/StockBadge';
import NewBadge from '../components/NewBadge';
import { Stars } from '../components/ReviewSection';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewStats, setReviewStats] = useState({});
  const { addToCart } = useCart();
  const { toast } = useToast();

  useEffect(() => {
    axios
      .get(`${API}/reviews/stats`)
      .then((res) => setReviewStats(res.data || {}))
      .catch(() => setReviewStats({}));
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'All Products' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Inject "showcase" cards for pre-mixed variants right after bundles.
  // These live only in the display list — the underlying product entry is
  // still the single product with its variants array.
  const displayProducts = (() => {
    const bundles = filteredProducts.filter(p => p.isBundle);
    const nonBundles = filteredProducts.filter(p => !p.isBundle);
    const showcaseCards = nonBundles
      .filter(p => Array.isArray(p.variants))
      .flatMap(p => {
        const premix = p.variants.find(v => v.key === 'premixed');
        if (!premix) return [];
        return [{
          ...p,
          _showcaseKey: `${p.id}-premixed`,
          _isVariantCard: true,
          _variantKey: premix.key,
          _variantLabel: premix.label,
          name: `${p.name} — Pre-Mixed`,
          price: p.price + premix.priceDelta,
          originalPrice: p.price,
          onSale: true,
        }];
      });
    return [...bundles, ...showcaseCards, ...nonBundles];
  })();

  const handleAddToCart = (product) => {
    if (product._isVariantCard) {
      addToCart({
        ...product,
        variant: product._variantKey,
        variantLabel: product._variantLabel,
      });
      toast({
        title: "Added to cart",
        description: `${product.name} added to your cart.`,
      });
      return;
    }
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const cardLinkTo = (product) =>
    product._isVariantCard
      ? `/product/${product.id}?variant=${product._variantKey}`
      : `/product/${product.id}`;

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#0C0A09' }}>
      <Seo
        path="/products"
        title="Shop Research Peptides | Eman's Peppers"
        description="Browse the full Eman's Peppers catalog — Retatrutide, Tirzepatide, BPC-157, TB-500, GHK-Cu, MOTS-C, NAD+ and more. Research-grade peptides with fast US shipping."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="section-title">Our Products</h1>
          <p className="section-subtitle">
            Browse our complete collection of research-grade peptides
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-10 space-y-6">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: '#B5A79E' }} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 w-full rounded-sm"
              style={{ 
                backgroundColor: '#1A1513', 
                border: '1px solid #362B25',
                color: '#F7F3EE',
                fontFamily: 'Manrope, sans-serif'
              }}
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Filter className="h-5 w-5" style={{ color: '#D4A373' }} />
            {categories.map(category => (
              <button
                key={category}
                className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                  selectedCategory === category 
                    ? 'btn-primary' 
                    : 'btn-secondary'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Count */}
        <p className="text-center mb-8" style={{ color: '#B5A79E' }}>
          Showing {displayProducts.length} {displayProducts.length === 1 ? 'product' : 'products'}
        </p>

        {/* Products Grid */}
        <div className="product-grid">
          {displayProducts.map(product => (
            <div 
              key={product._showcaseKey || product.id} 
              className="product-card"
            >
              <div
                className="product-image-wrapper cursor-pointer relative"
                onClick={() => navigate(cardLinkTo(product))}
              >
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="product-image"
                  style={product.stockStatus === 'out_of_stock' ? { opacity: 0.45, filter: 'grayscale(0.6)' } : undefined}
                />
                {product.stockStatus && product.stockStatus !== 'in_stock' && (
                  <div className="absolute top-3 left-3 z-10">
                    <StockBadge status={product.stockStatus} count={product.stockCount} note={product.restockNote} size="md" />
                  </div>
                )}
                {product.isNew && (
                  <div className={`absolute ${product.stockStatus && product.stockStatus !== 'in_stock' ? 'top-12' : 'top-3'} left-3 z-10`}>
                    <NewBadge size="md" />
                  </div>
                )}
                {product._isVariantCard && (
                  <div className="absolute top-3 right-3 z-10">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded"
                      style={{
                        background: 'linear-gradient(135deg, #86EFAC 0%, #4ADE80 100%)',
                        color: '#0C0A09',
                        boxShadow: '0 4px 12px rgba(74, 222, 128, 0.35)',
                      }}
                    >
                      Pre-Mixed
                    </span>
                  </div>
                )}
                {product.isBundle && (
                  <div className="absolute top-3 right-3 z-10">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded"
                      style={{
                        background: 'linear-gradient(135deg, #D4A373 0%, #C78C50 100%)',
                        color: '#0C0A09',
                        boxShadow: '0 4px 12px rgba(212, 163, 115, 0.4)',
                      }}
                    >
                      🎁 Bundle
                    </span>
                  </div>
                )}
              </div>
              
              <div className="product-info">
                <span className="product-category">{product.category}</span>
                <h3 
                  className="product-name cursor-pointer hover:text-[#D4A373] transition-colors"
                  onClick={() => navigate(cardLinkTo(product))}
                >
                  {product.name}
                </h3>
                {reviewStats[product.id] && reviewStats[product.id].count > 0 && (
                  <div className="flex items-center gap-1.5 mb-2" data-testid={`card-rating-${product.id}`}>
                    <Stars value={reviewStats[product.id].avg} size="sm" />
                    <span className="text-xs" style={{ color: '#B5A79E' }}>
                      {reviewStats[product.id].avg.toFixed(1)} ({reviewStats[product.id].count})
                    </span>
                  </div>
                )}
                <p className="product-description line-clamp-2 mb-4">
                  {product._isVariantCard
                    ? 'Ready to inject — no reconstitution needed. Ships with cold pack.'
                    : product.description}
                </p>
                
                <div className="mt-auto">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="product-price">${product.price}</span>
                      {product.onSale && product.originalPrice && (
                        <span className="text-sm line-through" style={{ color: '#5A4A3F' }}>
                          ${product.originalPrice}
                        </span>
                      )}
                    </div>
                    {product.onSale && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'rgba(220, 38, 38, 0.15)',
                          color: '#FCA5A5',
                          border: '1px solid rgba(220, 38, 38, 0.4)',
                        }}
                      >
                        Sale
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => navigate(cardLinkTo(product))}
                      className="btn-secondary text-xs px-3 py-2"
                    >
                      View Details
                    </button>
                    {product.stockStatus === 'out_of_stock' ? (
                      <button
                        disabled
                        data-testid={`oos-button-${product.id}`}
                        className="text-xs px-3 py-2 flex items-center justify-center gap-2 rounded font-bold uppercase tracking-wider cursor-not-allowed leading-tight"
                        style={{
                          backgroundColor: '#1A1513',
                          color: '#5A4A3F',
                          border: '1px solid #362B25',
                        }}
                      >
                        {product.restockNote ? (
                          <span className="flex flex-col items-center">
                            <span>Sold Out</span>
                            <span className="text-[9px] font-semibold normal-case tracking-normal" style={{ color: '#FCD34D' }}>
                              {product.restockNote}
                            </span>
                          </span>
                        ) : (
                          'Sold Out'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="btn-primary text-xs px-3 py-2 flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {displayProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-xl mb-4" style={{ color: '#B5A79E' }}>
              No products found matching your search.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All Products');
              }}
              className="btn-primary"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
